import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent,
  Paper,
  Container,
  Chip,
  useTheme,
  useMediaQuery,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShowChart,
  Timeline,
  Assessment,
  PieChart,
  BarChart,
  CalendarToday,
  AttachMoney,
  Percent,
  Speed,
  Psychology
} from '@mui/icons-material';
import { listTrades } from './tradesSlice';
import { calculateActualPnL } from '../../utils/tradeCalculations';
import { fetchTradeState } from './executionsSlice';
import DWLFLoader from '../../components/DWLFLoader';

const TradeAnalyticsPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  
  const { items: trades, loading, error } = useSelector((state) => state.trades);
  const tradeStates = useSelector((s) => s.executions.tradeStates);
  const [timeframe, setTimeframe] = useState('all'); // all, 30d, 90d, 1y
  const [viewMode, setViewMode] = useState('overview'); // overview, events, assets
  const [tradeMode, setTradeMode] = useState('real'); // real, paper

  useEffect(() => {
    dispatch(listTrades({ tradeType: tradeMode }));
  }, [dispatch, tradeMode]);

  // Fetch trade state data for each trade
  useEffect(() => {
    if (trades && trades.length > 0) {
      trades.forEach(trade => {
        if (!tradeStates[trade.tradeId]) {
          dispatch(fetchTradeState({ tradeId: trade.tradeId }));
        }
      });
    }
  }, [trades, tradeStates, dispatch]);

  // Filter trades based on timeframe
  const filteredTrades = useMemo(() => {
    if (!trades || trades.length === 0) return [];
    
    const now = new Date();
    const cutoffDate = new Date();
    
    switch (timeframe) {
      case '30d':
        cutoffDate.setDate(now.getDate() - 30);
        break;
      case '90d':
        cutoffDate.setDate(now.getDate() - 90);
        break;
      case '1y':
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return trades; // all
    }
    
    return trades.filter(trade => new Date(trade.entryAt) >= cutoffDate);
  }, [trades, timeframe]);

  // Helper function to safely calculate P&L for a trade
  const calculateTradePnL = (trade) => {
    try {
      if (!trade || trade.status !== 'closed') {
        return 0;
      }

      const tradeState = tradeStates[trade.tradeId];

      // Priority 1: Use executions-based P&L if available and valid
      const realizedPnl = tradeState?.positionState?.realizedPnl;
      if (realizedPnl != null && !isNaN(realizedPnl)) {
        // Trust non-zero values; for zero, only trust if pnlPct also confirms break-even
        if (realizedPnl !== 0 || !trade.pnlPct) {
          return Number(realizedPnl);
        }
        // realizedPnl is 0 but pnlPct is non-zero — trade state is incomplete, fall through
      }

      // Priority 2: Calculate from price difference
      const calculatedPnL = calculateActualPnL(trade);
      if (calculatedPnL !== undefined && !isNaN(calculatedPnL)) {
        return Number(calculatedPnL);
      }

      // Priority 3: Use stored pnlAbs
      if (trade.pnlAbs != null && !isNaN(trade.pnlAbs)) {
        return Number(trade.pnlAbs);
      }

      console.warn('Could not calculate P&L for trade:', trade.tradeId);
      return 0;
    } catch (error) {
      console.error('Error calculating P&L for trade:', trade.tradeId, error);
      return 0;
    }
  };

  // Calculate performance metrics
  const metrics = useMemo(() => {
    if (!filteredTrades || filteredTrades.length === 0) {
      return {
        totalTrades: 0,
        closedTrades: 0,
        openTrades: 0,
        winRate: 0,
        totalPnL: 0,
        avgRMultiple: 0,
        expectancy: 0,
        avgHoldTime: 0,
        bestTrade: null,
        worstTrade: null,
        winningTrades: 0,
        losingTrades: 0,
        // Direction-specific metrics
        longTrades: 0,
        shortTrades: 0,
        longWinRate: 0,
        shortWinRate: 0,
        longTotalPnL: 0,
        shortTotalPnL: 0,
        longAvgRMultiple: 0,
        shortAvgRMultiple: 0
      };
    }

    try {
      const closedTrades = filteredTrades.filter(trade => trade?.status === 'closed');
      const openTrades = filteredTrades.filter(trade => trade?.status === 'open');
      
      // Calculate P&L safely for all closed trades
      const tradesWithCalculatedPnL = closedTrades.map(trade => {
        const finalPnL = calculateTradePnL(trade);
        return {
          ...trade,
          finalPnL
        };
      });
      
      const winningTrades = tradesWithCalculatedPnL.filter(trade => trade.finalPnL > 0);
      const losingTrades = tradesWithCalculatedPnL.filter(trade => trade.finalPnL < 0);
      
      const totalPnL = tradesWithCalculatedPnL.reduce((sum, trade) => sum + (trade.finalPnL || 0), 0);
      
      // Calculate average R-Multiple safely
      const rMultiples = tradesWithCalculatedPnL
        .map(trade => trade.rMultiple)
        .filter(r => r !== undefined && r !== null && !isNaN(r))
        .map(r => Number(r));
      const avgRMultiple = rMultiples.length > 0 
        ? rMultiples.reduce((sum, r) => sum + r, 0) / rMultiples.length 
        : 0;
      
      const expectancy = tradesWithCalculatedPnL.length > 0 ? totalPnL / tradesWithCalculatedPnL.length : 0;
      
      // Calculate average hold time safely
      const holdTimes = closedTrades
        .filter(trade => trade.entryAt && trade.exitAt)
        .map(trade => {
          try {
            const entry = new Date(trade.entryAt);
            const exit = new Date(trade.exitAt);
            if (isNaN(entry.getTime()) || isNaN(exit.getTime())) return null;
            return (exit - entry) / (1000 * 60 * 60 * 24); // days
          } catch (error) {
            console.warn('Invalid date in trade:', trade.tradeId, error);
            return null;
          }
        })
        .filter(time => time !== null && time >= 0);
        
      const avgHoldTime = holdTimes.length > 0 
        ? holdTimes.reduce((sum, time) => sum + time, 0) / holdTimes.length 
        : 0;

      const bestTrade = tradesWithCalculatedPnL.length > 0 
        ? tradesWithCalculatedPnL.reduce((best, trade) => (trade.finalPnL > best.finalPnL ? trade : best))
        : null;
      
      const worstTrade = tradesWithCalculatedPnL.length > 0 
        ? tradesWithCalculatedPnL.reduce((worst, trade) => (trade.finalPnL < worst.finalPnL ? trade : worst))
        : null;

      // Calculate direction-specific metrics
      const longClosedTrades = tradesWithCalculatedPnL.filter(t => t.direction === 'long');
      const shortClosedTrades = tradesWithCalculatedPnL.filter(t => t.direction === 'short');
      
      const longWinningTrades = longClosedTrades.filter(t => t.finalPnL > 0);
      const shortWinningTrades = shortClosedTrades.filter(t => t.finalPnL > 0);
      
      const longTotalPnL = longClosedTrades.reduce((sum, t) => sum + (t.finalPnL || 0), 0);
      const shortTotalPnL = shortClosedTrades.reduce((sum, t) => sum + (t.finalPnL || 0), 0);
      
      const longRMultiples = longClosedTrades
        .map(t => t.rMultiple)
        .filter(r => r !== undefined && r !== null && !isNaN(r))
        .map(r => Number(r));
      const longAvgRMultiple = longRMultiples.length > 0
        ? longRMultiples.reduce((sum, r) => sum + r, 0) / longRMultiples.length
        : 0;
      
      const shortRMultiples = shortClosedTrades
        .map(t => t.rMultiple)
        .filter(r => r !== undefined && r !== null && !isNaN(r))
        .map(r => Number(r));
      const shortAvgRMultiple = shortRMultiples.length > 0
        ? shortRMultiples.reduce((sum, r) => sum + r, 0) / shortRMultiples.length
        : 0;

      return {
        totalTrades: filteredTrades.length,
        closedTrades: closedTrades.length,
        openTrades: openTrades.length,
        winRate: closedTrades.length > 0 ? (winningTrades.length / closedTrades.length) * 100 : 0,
        totalPnL: Number(totalPnL.toFixed(2)),
        avgRMultiple: Number(avgRMultiple.toFixed(3)),
        expectancy: Number(expectancy.toFixed(2)),
        avgHoldTime: Number(avgHoldTime.toFixed(1)),
        bestTrade,
        worstTrade,
        winningTrades: winningTrades.length,
        losingTrades: losingTrades.length,
        // Direction-specific metrics
        longTrades: longClosedTrades.length,
        shortTrades: shortClosedTrades.length,
        longWinRate: longClosedTrades.length > 0 ? (longWinningTrades.length / longClosedTrades.length) * 100 : 0,
        shortWinRate: shortClosedTrades.length > 0 ? (shortWinningTrades.length / shortClosedTrades.length) * 100 : 0,
        longTotalPnL: Number(longTotalPnL.toFixed(2)),
        shortTotalPnL: Number(shortTotalPnL.toFixed(2)),
        longAvgRMultiple: Number(longAvgRMultiple.toFixed(3)),
        shortAvgRMultiple: Number(shortAvgRMultiple.toFixed(3))
      };
    } catch (error) {
      console.error('Error calculating trade metrics:', error);
      return {
        totalTrades: 0,
        closedTrades: 0,
        openTrades: 0,
        winRate: 0,
        totalPnL: 0,
        avgRMultiple: 0,
        expectancy: 0,
        avgHoldTime: 0,
        bestTrade: null,
        worstTrade: null,
        winningTrades: 0,
        losingTrades: 0,
        // Direction-specific metrics
        longTrades: 0,
        shortTrades: 0,
        longWinRate: 0,
        shortWinRate: 0,
        longTotalPnL: 0,
        shortTotalPnL: 0,
        longAvgRMultiple: 0,
        shortAvgRMultiple: 0
      };
    }
  }, [filteredTrades, tradeStates]);

  // Helper function to safely extract event type from event ID
  const parseEventType = (eventId) => {
    try {
      if (!eventId || typeof eventId !== 'string') {
        return 'unknown';
      }
      
      const normalized = eventId.toLowerCase().replace(/[^a-z0-9_.-]/g, '_');
      
      // Handle different event ID formats:
      // Format 1: "NVDA_higher_low_2025-05-23" (most common)
      // Format 2: "higher_low" (standalone event type)
      // Format 3: "BRK.B_higher_low_2025-05-23" (dotted symbols)
      
      // First, try parsing as underscore-separated (main format)
      if (normalized.includes('_')) {
        const parts = normalized.split('_');
        
        // If only 2 parts, assume it's "symbol_eventtype"
        if (parts.length === 2) {
          return parts[1];
        }
        
        // If 3+ parts, look for event type patterns
        if (parts.length >= 3) {
          // Look for date pattern in last part (YYYY-MM-DD or timestamp)
          const lastPart = parts[parts.length - 1];
          if (/^\d{4}-\d{2}-\d{2}$/.test(lastPart) || /^\d{10,}$/.test(lastPart)) {
            // Has date - middle parts are the event type
            return parts.slice(1, -1).join('_');
          } else {
            // No clear date - assume everything after first part is event type
            return parts.slice(1).join('_');
          }
        }
      }
      
      // If no underscores, check if it looks like a plain event type
      if (/^[a-z_]+$/.test(normalized) && !normalized.includes('.')) {
        return normalized;
      }
      
      // For other formats, extract the meaningful part
      return normalized.replace(/[^a-z_]+/g, '_').replace(/^_+|_+$/g, '').replace(/_+/g, '_') || 'unknown';
    } catch (error) {
      console.warn('Error parsing event type from:', eventId, error);
      return 'unknown';
    }
  };

  // Performance by event type
  const eventPerformance = useMemo(() => {
    if (!filteredTrades || filteredTrades.length === 0) return {};
    
    try {
      const eventStats = {};
      
      filteredTrades.forEach(trade => {
        try {
          const finalPnL = calculateTradePnL(trade);
          
          if (trade.linkedEvents && Array.isArray(trade.linkedEvents)) {
            trade.linkedEvents.forEach(eventId => {
              const eventType = parseEventType(eventId);
              
              if (!eventStats[eventType]) {
                eventStats[eventType] = {
                  count: 0,
                  wins: 0,
                  losses: 0,
                  totalPnL: 0,
                  totalRMultiple: 0
                };
              }
              
              eventStats[eventType].count++;
              
              if (trade.status === 'closed') {
                if (finalPnL > 0) {
                  eventStats[eventType].wins++;
                } else {
                  eventStats[eventType].losses++;
                }
                
                eventStats[eventType].totalPnL += finalPnL;
                
                // Safely handle R-Multiple
                const rMultiple = trade.rMultiple;
                if (rMultiple !== undefined && rMultiple !== null && !isNaN(rMultiple)) {
                  eventStats[eventType].totalRMultiple += Number(rMultiple);
                }
              }
            });
          }
        } catch (error) {
          console.warn('Error processing trade for event performance:', trade.tradeId, error);
        }
      });
      
      // Calculate averages safely
      Object.keys(eventStats).forEach(eventType => {
        const stats = eventStats[eventType];
        const closedTrades = stats.wins + stats.losses;
        
        if (closedTrades > 0) {
          stats.winRate = Number(((stats.wins / closedTrades) * 100).toFixed(1));
          stats.avgRMultiple = Number((stats.totalRMultiple / closedTrades).toFixed(3));
        } else {
          stats.winRate = 0;
          stats.avgRMultiple = 0;
        }
        
        stats.totalPnL = Number(stats.totalPnL.toFixed(2));
      });
      
      return eventStats;
    } catch (error) {
      console.error('Error calculating event performance:', error);
      return {};
    }
  }, [filteredTrades, tradeStates]);

  // Performance by asset
  const assetPerformance = useMemo(() => {
    if (!filteredTrades || filteredTrades.length === 0) return {};
    
    try {
      const assetStats = {};
      
      filteredTrades.forEach(trade => {
        try {
          const asset = trade.assetSymbol || 'Unknown';
          const finalPnL = calculateTradePnL(trade);
          
          if (!assetStats[asset]) {
            assetStats[asset] = {
              count: 0,
              wins: 0,
              losses: 0,
              totalPnL: 0,
              totalRMultiple: 0
            };
          }
          
          assetStats[asset].count++;
          
          if (trade.status === 'closed') {
            if (finalPnL > 0) {
              assetStats[asset].wins++;
            } else {
              assetStats[asset].losses++;
            }
            
            assetStats[asset].totalPnL += finalPnL;
            
            // Safely handle R-Multiple
            const rMultiple = trade.rMultiple;
            if (rMultiple !== undefined && rMultiple !== null && !isNaN(rMultiple)) {
              assetStats[asset].totalRMultiple += Number(rMultiple);
            }
          }
        } catch (error) {
          console.warn('Error processing trade for asset performance:', trade.tradeId, error);
        }
      });
      
      // Calculate averages safely
      Object.keys(assetStats).forEach(asset => {
        const stats = assetStats[asset];
        const closedTrades = stats.wins + stats.losses;
        
        if (closedTrades > 0) {
          stats.winRate = Number(((stats.wins / closedTrades) * 100).toFixed(1));
          stats.avgRMultiple = Number((stats.totalRMultiple / closedTrades).toFixed(3));
        } else {
          stats.winRate = 0;
          stats.avgRMultiple = 0;
        }
        
        stats.totalPnL = Number(stats.totalPnL.toFixed(2));
      });
      
      return assetStats;
    } catch (error) {
      console.error('Error calculating asset performance:', error);
      return {};
    }
  }, [filteredTrades, tradeStates]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <DWLFLoader size="large" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Error loading trade analytics: {error}</Alert>
      </Box>
    );
  }

  const MetricCard = ({ title, value, subtitle, icon, color = 'primary.main', trend = null }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
          <Box sx={{ color: color }}>
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" fontWeight={700} color={color} mb={1}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Box display="flex" alignItems="center" mt={1}>
            {trend > 0 ? (
              <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
            ) : (
              <TrendingDown sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />
            )}
            <Typography 
              variant="body2" 
              color={trend > 0 ? 'success.main' : 'error.main'}
            >
              {Math.abs(trend)}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const PerformanceTable = ({ data, title, columns }) => {
    try {
      const entries = Object.entries(data || {});
      
      return (
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              {title}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              {entries.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                  No data available
                </Typography>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${(theme.vars || theme).palette.divider}` }}>
                      {columns.map((col, index) => (
                        <th key={index} style={{ 
                          textAlign: 'left', 
                          padding: '12px 8px',
                          fontWeight: 600,
                          color: (theme.vars || theme).palette.text.primary
                        }}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(([key, stats]) => {
                      try {
                        return (
                          <tr key={key} style={{ borderBottom: `1px solid ${(theme.vars || theme).palette.divider}` }}>
                            <td style={{ padding: '12px 8px' }}>
                              <Typography variant="body2" fontWeight={600}>
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </Typography>
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <Typography variant="body2">
                                {stats?.count || 0}
                              </Typography>
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <Typography variant="body2" color={(stats?.winRate || 0) >= 50 ? 'success.main' : 'error.main'}>
                                {(stats?.winRate || 0).toFixed ? (stats.winRate).toFixed(1) : '0.0'}%
                              </Typography>
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <Typography variant="body2" color={(stats?.totalPnL || 0) >= 0 ? 'success.main' : 'error.main'}>
                                ${(stats?.totalPnL || 0).toFixed ? (stats.totalPnL).toFixed(2) : '0.00'}
                              </Typography>
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <Typography variant="body2">
                                {(stats?.avgRMultiple || 0).toFixed ? (stats.avgRMultiple).toFixed(2) : '0.00'}R
                              </Typography>
                            </td>
                          </tr>
                        );
                      } catch (error) {
                        console.warn('Error rendering table row for:', key, error);
                        return (
                          <tr key={key} style={{ borderBottom: `1px solid ${(theme.vars || theme).palette.divider}` }}>
                            <td colSpan={columns.length} style={{ padding: '12px 8px' }}>
                              <Typography variant="body2" color="error.main">
                                Error displaying data for {key}
                              </Typography>
                            </td>
                          </tr>
                        );
                      }
                    })}
                  </tbody>
                </table>
              )}
            </Box>
          </CardContent>
        </Card>
      );
    } catch (error) {
      console.error('Error rendering PerformanceTable:', error);
      return (
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              {title}
            </Typography>
            <Alert severity="error">
              Error displaying table data
            </Alert>
          </CardContent>
        </Card>
      );
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} mb={1}>
            Trade Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Performance insights and trading metrics
          </Typography>
        </Box>
        
        {/* Controls */}
        <Box display="flex" gap={2} alignItems="center">
          <ToggleButtonGroup
            value={tradeMode}
            exclusive
            onChange={(e, newValue) => newValue && setTradeMode(newValue)}
            size="small"
          >
            <ToggleButton value="real">
              Real Trades
            </ToggleButton>
            <ToggleButton value="paper">
              Paper Trades
            </ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small">
            <InputLabel>Timeframe</InputLabel>
            <Select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              label="Timeframe"
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
              <MenuItem value="1y">Last Year</MenuItem>
            </Select>
          </FormControl>
          
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newValue) => newValue && setViewMode(newValue)}
            size="small"
          >
            <ToggleButton value="overview">
              <Assessment sx={{ mr: 1 }} />
              Overview
            </ToggleButton>
            <ToggleButton value="events">
              <Timeline sx={{ mr: 1 }} />
              Events
            </ToggleButton>
            <ToggleButton value="assets">
              <ShowChart sx={{ mr: 1 }} />
              Assets
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Overview Mode */}
      {viewMode === 'overview' && (
        <>
          {/* Key Metrics */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 3
            }}
          >
            <Box>
              <MetricCard
                title="Total Trades"
                value={metrics.totalTrades}
                subtitle={`${metrics.closedTrades} closed, ${metrics.openTrades} open`}
                icon={<Timeline />}
              />
            </Box>
            <Box>
              <MetricCard
                title="Win Rate"
                value={`${metrics.winRate.toFixed(1)}%`}
                subtitle={`${metrics.winningTrades} wins, ${metrics.losingTrades} losses`}
                icon={<Percent />}
                color={metrics.winRate >= 50 ? 'success.main' : 'error.main'}
              />
            </Box>
            <Box>
              <MetricCard
                title="Total P&L"
                value={`$${metrics.totalPnL.toFixed(2)}`}
                subtitle="Closed trades only"
                icon={<AttachMoney />}
                color={metrics.totalPnL >= 0 ? 'success.main' : 'error.main'}
              />
            </Box>
            <Box>
              <MetricCard
                title="Avg R-Multiple"
                value={metrics.avgRMultiple.toFixed(2)}
                subtitle="Risk-reward ratio"
                icon={<Speed />}
                color={metrics.avgRMultiple >= 1 ? 'success.main' : 'warning.main'}
              />
            </Box>
          </Box>

          {/* Additional Metrics */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 3
            }}
          >
            <Box>
              <MetricCard
                title="Expectancy"
                value={`$${metrics.expectancy.toFixed(2)}`}
                subtitle="Average P&L per trade"
                icon={<Psychology />}
                color={metrics.expectancy >= 0 ? 'success.main' : 'error.main'}
              />
            </Box>
            <Box>
              <MetricCard
                title="Avg Hold Time"
                value={`${metrics.avgHoldTime.toFixed(1)} days`}
                subtitle="Closed trades only"
                icon={<CalendarToday />}
              />
            </Box>
            <Box>
              <MetricCard
                title="Best Trade"
                value={metrics.bestTrade ? `$${metrics.bestTrade.finalPnL.toFixed(2)}` : 'N/A'}
                subtitle={metrics.bestTrade ? metrics.bestTrade.assetSymbol : ''}
                icon={<TrendingUp />}
                color={metrics.bestTrade == null ? 'primary.main' : metrics.bestTrade.finalPnL >= 0 ? 'success.main' : 'error.main'}
              />
            </Box>
            <Box>
              <MetricCard
                title="Worst Trade"
                value={metrics.worstTrade ? `$${metrics.worstTrade.finalPnL.toFixed(2)}` : 'N/A'}
                subtitle={metrics.worstTrade ? metrics.worstTrade.assetSymbol : ''}
                icon={<TrendingDown />}
                color={metrics.worstTrade == null ? 'primary.main' : metrics.worstTrade.finalPnL >= 0 ? 'success.main' : 'error.main'}
              />
            </Box>
          </Box>

          {/* Direction Performance - NEW */}
          <Box mb={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Performance by Direction
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                    gap: 3
                  }}
                >
                  {/* Long Performance */}
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <TrendingUp sx={{ color: 'success.main' }} />
                      <Typography variant="h6" fontWeight={600} color="success.main">
                        LONG Trades
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      backgroundColor: 'background.surface', 
                      p: 2, 
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: 'success.main'
                    }}>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">Closed Trades</Typography>
                        <Typography variant="h5" fontWeight={700}>{metrics.longTrades}</Typography>
                      </Box>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">Win Rate</Typography>
                        <Typography variant="h5" fontWeight={700} color={metrics.longWinRate >= 50 ? 'success.main' : 'error.main'}>
                          {metrics.longWinRate.toFixed(1)}%
                        </Typography>
                      </Box>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">Total P&L</Typography>
                        <Typography variant="h5" fontWeight={700} color={metrics.longTotalPnL >= 0 ? 'success.main' : 'error.main'}>
                          ${metrics.longTotalPnL.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Avg R-Multiple</Typography>
                        <Typography variant="h5" fontWeight={700} color={metrics.longAvgRMultiple >= 1 ? 'success.main' : 'warning.main'}>
                          {metrics.longAvgRMultiple.toFixed(2)}R
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Short Performance */}
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <TrendingDown sx={{ color: 'error.main' }} />
                      <Typography variant="h6" fontWeight={600} color="error.main">
                        SHORT Trades
                      </Typography>
                      <Chip
                        label="SHORT"
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(239, 68, 68, 0.15)',
                          color: '#ef4444',
                          border: '1px solid #ef4444',
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          letterSpacing: '0.05em',
                        }}
                      />
                    </Box>
                    <Box sx={{ 
                      backgroundColor: 'background.surface', 
                      p: 2, 
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: 'error.main'
                    }}>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">Closed Trades</Typography>
                        <Typography variant="h5" fontWeight={700}>{metrics.shortTrades}</Typography>
                      </Box>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">Win Rate</Typography>
                        <Typography variant="h5" fontWeight={700} color={metrics.shortWinRate >= 50 ? 'success.main' : 'error.main'}>
                          {metrics.shortWinRate.toFixed(1)}%
                        </Typography>
                      </Box>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">Total P&L</Typography>
                        <Typography variant="h5" fontWeight={700} color={metrics.shortTotalPnL >= 0 ? 'success.main' : 'error.main'}>
                          ${metrics.shortTotalPnL.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Avg R-Multiple</Typography>
                        <Typography variant="h5" fontWeight={700} color={metrics.shortAvgRMultiple >= 1 ? 'success.main' : 'warning.main'}>
                          {metrics.shortAvgRMultiple.toFixed(2)}R
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Performance Tables */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              alignItems: 'stretch'
            }}
          >
            <Box>
              <PerformanceTable
                data={eventPerformance}
                title="Performance by Event Type"
                columns={[
                  { label: 'Event Type', key: 'type' },
                  { label: 'Trades', key: 'count' },
                  { label: 'Win Rate', key: 'winRate' },
                  { label: 'Total P&L', key: 'totalPnL' },
                  { label: 'Avg R', key: 'avgR' }
                ]}
              />
            </Box>
            <Box>
              <PerformanceTable
                data={assetPerformance}
                title="Performance by Asset"
                columns={[
                  { label: 'Asset', key: 'asset' },
                  { label: 'Trades', key: 'count' },
                  { label: 'Win Rate', key: 'winRate' },
                  { label: 'Total P&L', key: 'totalPnL' },
                  { label: 'Avg R', key: 'avgR' }
                ]}
              />
            </Box>
          </Box>
        </>
      )}

      {/* Events Mode */}
      {viewMode === 'events' && (
        <PerformanceTable
          data={eventPerformance}
          title="Event Performance Analysis"
          columns={[
            { label: 'Event Type', key: 'type' },
            { label: 'Trades', key: 'count' },
            { label: 'Win Rate', key: 'winRate' },
            { label: 'Total P&L', key: 'totalPnL' },
            { label: 'Avg R', key: 'avgR' }
          ]}
        />
      )}

      {/* Assets Mode */}
      {viewMode === 'assets' && (
        <PerformanceTable
          data={assetPerformance}
          title="Asset Performance Analysis"
          columns={[
            { label: 'Asset', key: 'asset' },
            { label: 'Trades', key: 'count' },
            { label: 'Win Rate', key: 'winRate' },
            { label: 'Total P&L', key: 'totalPnL' },
            { label: 'Avg R', key: 'avgR' }
          ]}
        />
      )}
    </Container>
  );
};

export default TradeAnalyticsPage;
